import Color from 'color';
import { get } from 'lodash';
import { bool, func, object, string } from 'prop-types';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import type { DeepPartial } from 'ts-essentials';

import { Box } from '@hivebrite/design-system/Box';
import { createTheme } from '@hivebrite/design-system/Theme';

import { useColorPickerContext } from 'hb-builder/hooks';
import type { OnChangeSetting } from 'hb-builder/types/settings';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type { ItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes';
import { convertNetworkColorsToFrontofficeTheme } from 'hb-react/style/convertNetworkColorsToFrontofficeTheme';
import { convertNetworkThemingToFrontofficeThemeVariables } from 'hb-react/style/convertNetworkThemingToFrontofficeThemeVariables';
import type { Colors, NetworkTheming } from 'hb-react/types/network';

import ColorInput from '../ComposedInputs/ColorInput';
import InputTitle from '../SettingsDecoration/InputTitle';

export interface ColorSettingsProps {
  item: DocumentItem;
  onChange: OnChangeSetting;
  settingKey: string;
  color: {
    brandColors: Array<{ label: string; value: string }>;
    opacity?: boolean;
  };
  networkColors?: Colors;
  networkTheming?: DeepPartial<NetworkTheming>;
  boxProps?: Record<string, any>;
  componentHasMultiplePickers?: boolean;
  defaultAttributes?: ItemAttributes;
  style?: Record<string, any>;
  title?: string;
  transparent?: boolean;
}

const ColorSetting = ({
  boxProps = { alignItems: 'center', mb: 4 },
  color,
  componentHasMultiplePickers = false,
  defaultAttributes,
  item,
  networkColors,
  networkTheming,
  onChange,
  settingKey,
  style,
  title,
  transparent,
}: ColorSettingsProps) => {
  const fontBrightness = networkTheming?.fo?.fo_outboxed_font_brightness;

  const theme = createTheme({
    customColors: convertNetworkColorsToFrontofficeTheme(networkColors),
    customThemeVariables: convertNetworkThemingToFrontofficeThemeVariables({
      /* @ts-expect-error untyped */
      theming: networkTheming,
      fontBrightness,
    }),

    themeName: 'fo',
  });

  const { externalControl, resetAllColorPickers } = useColorPickerContext();

  const getColorCode = (initialColorValue) => {
    const color = get(theme.colors, initialColorValue)
      ? theme.colors[initialColorValue]
      : initialColorValue;

    if (transparent) {
      // cast rgba into rgb if transparent option is not allowed.
      return color;
    }

    try {
      return Color(color).alpha(1).string();
    } catch {
      return color;
    }
  };

  const [colorValue, setColorValue] = useState(
    getColorCode(
      item.attributes?.[settingKey] || defaultAttributes?.[settingKey],
    ),
  );

  useEffect(() => {
    const itemColor = item.attributes?.[settingKey];

    if (itemColor) {
      const color = getColorCode(itemColor);

      if (colorValue !== color) {
        setColorValue(color);
      }
    }
  }, [color, item.attributes?.[settingKey], setColorValue]);

  const handleChangeColor = ({ value }) => {
    setColorValue(value);

    onChange({
      id: item.id,
      settingKey: `attributes.${settingKey}`,
      value,
    });
  };

  const id = `colorSetting-${(title || '').split(' ').join('-')}-${item.id}`;

  return (
    <Wrapper {...boxProps}>
      {title && <InputTitle id={id} label={title} />}
      <ColorInput
        brandColors={color?.brandColors}
        componentHasMultiplePickers={componentHasMultiplePickers}
        externalControl={externalControl}
        hasTransparent={transparent}
        hasOpacity={color?.opacity}
        id={id}
        onChange={handleChangeColor}
        resetAllColorPickers={resetAllColorPickers}
        style={style}
        value={colorValue}
        theme={theme}
      />
    </Wrapper>
  );
};

export default ColorSetting;

const Wrapper = styled(Box).attrs(() => ({
  flex: 1,
  justifyContent: 'space-between',
}))``;

ColorSetting.propTypes = {
  item: object.isRequired,
  onChange: func.isRequired,
  settingKey: string.isRequired,
  title: string.isRequired,
  transparent: bool,
};
